import React, {
  useState, useRef, useEffect, useLayoutEffect, useCallback,
} from 'react';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Textarea,
  Spinner,
  useToast,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Icon,
  useDisclosure,
} from '@chakra-ui/react';
import { DeleteIcon, AddIcon, ArrowUpIcon } from '@chakra-ui/icons';
import {
  LuArrowUp, LuChevronDown, LuEllipsis, LuHistory, LuPencil, LuPlus, LuTrash2,
} from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  sendMessage,
  sendFailureText,
  clearSession,
  getSessions,
  getSessionMessages,
  CHAT_SESSION_KEY,
} from './chatService';
import ChatMarkdown from './chatMarkdown';
import { ChatRecordsProvider } from './ChatRecordsContext';
import { recordLocation } from './chatRecords';
import { DOCK_REQUEST_KEY } from './AssistantWidget';
import { useAuth } from '../../contexts/AuthContext';
import { useFilters } from '../../contexts/FilterContext';
import ConfirmationDialog from '../shared/ConfirmationDialog';
import { formatRelativeShort } from '../../utils/intlFormatters';
import { groupSessions } from './sessionList';
import { chatOwnerName } from './chatAudience';
import PhoneChatHistory from './PhoneChatHistory';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import useIsPhone from '../../hooks/useIsPhone';
import useKeyboardInset from '../../hooks/useKeyboardInset';
import { useStickToBottom } from '../../hooks/useStickToBottom';
import { usePhoneShell, usePhoneTopBar } from '../phone/PhoneShellContext';
import PhoneConfirmSheet from '../phone/PhoneConfirmSheet';
import { SCROLL_TOP_EVENT } from '../phone/PhoneTabBar';
import { looksLikeSpending } from '../../utils/spendingText';

// Remember the active conversation so a reload / re-login keeps you in the
// same chat instead of silently starting a new one (which fragments history).

// The history menu's rows highlight in the same neutral as the user's own
// message.
const MENU_HIGHLIGHT = { bg: 'redesign.bg.bubble' };

// "Send back to the floating panel": a window with the panel docked in its
// bottom-right corner — which is exactly where the panel reappears. Lucide's
// picture-in-picture glyphs leave the frame broken open, which reads as noise
// at 16px, so this is drawn to keep the frame closed.
const DockIcon = (props) => (
  <Icon viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
    <rect x="2.5" y="4" width="19" height="16" rx="2.5" strokeLinejoin="round" />
    <rect x="12" y="12.5" width="7.5" height="5.5" rx="1.25" fill="currentColor" stroke="none" />
  </Icon>
);

// The phone's page fills the screen between the top bar and the tab bar; only
// the message list scrolls.
const PHONE_PAGE_H = 'calc(100dvh - 44px - 49px - env(safe-area-inset-top) - env(safe-area-inset-bottom))';
// The composer grows from one line to five (22px lines, 11px padding).
const PHONE_COMPOSER_MAX_H = 5 * 22 + 22;
const PHONE_SUGGESTIONS = ['eatingOut', 'rent', 'compareMonths', 'biggestExpenses'];

// The Ask title, 17px, or 15px where a language's title does not fit between
// the top bar's two buttons (Spanish at 320px).
function FitTitle({ children }) {
  const ref = useRef(null);
  const [small, setSmall] = useState(false);

  useLayoutEffect(() => {
    const measure = () => {
      const el = ref.current;
      const bar = el?.closest('h1')?.parentElement;
      if (!bar) return;
      const prev = el.style.fontSize;
      el.style.fontSize = '17px';
      const needed = el.getBoundingClientRect().width;
      el.style.fontSize = prev;
      // The bar's 8px sides, two 44px buttons and a little air.
      setSmall(needed > bar.clientWidth - 16 - 2 * 44 - 8);
    };
    measure();
    // A web font that loads after this first pass changes the width.
    let live = true;
    document.fonts?.ready.then(() => { if (live) measure(); });
    window.addEventListener('resize', measure);
    return () => {
      live = false;
      window.removeEventListener('resize', measure);
    };
  }, [children]);

  return (
    <Box as="span" ref={ref} display="inline-block" whiteSpace="nowrap" fontSize={small ? '15px' : '17px'}>
      {children}
    </Box>
  );
}

const ChatPage = () => {
  const { t, i18n } = useTranslation('chat');
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isPhone = useIsPhone();
  const { can, current } = useWorkspace();
  const owner = chatOwnerName(current);
  const suggestion = (key) => (
    owner ? t(`phone.suggestionsFor.${key}`, { owner }) : t(`phone.suggestions.${key}`)
  );
  const { openQuickAdd } = usePhoneShell();
  const { showSearch } = useFilters();
  const { inset, keyboardUp } = useKeyboardInset(isPhone);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [composerH, setComposerH] = useState(0);
  const composerRef = useRef(null);
  const textareaRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  // Session queued for deletion — set by the ··· menu (current chat) or by a
  // row's trash button (any chat); both confirm through the same dialog.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const confirmDelete = useDisclosure();
  const messagesContainerRef = useRef(null);
  // Bumped whenever the view leaves the conversation a pending answer belongs
  // to (New chat, another conversation, a delete), so that answer does not
  // land in the new view or make its conversation the active one again.
  const requestRef = useRef(0);
  const toast = useToast();

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    // The phone's empty state starts at its greeting, not its last suggestion.
    if (isPhone && messages.length === 0 && !isLoading) return;
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages, isLoading, isPhone]);
  // The list is only mounted once there is something in it (desktop), and the
  // phone has a list of its own.
  useStickToBottom(messagesContainerRef, `${isPhone}:${messages.length > 0}`);

  // Load the conversation list for the history menu
  const loadSessions = useCallback(async () => {
    try {
      const data = await getSessions();
      setSessions(Array.isArray(data) ? data : []);
    } catch (error) {
      // Non-blocking: the menu just stays empty if this fails.
    }
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Restore the last active conversation on mount (survives reload / re-login).
  useEffect(() => {
    const saved = localStorage.getItem(CHAT_SESSION_KEY);
    // A question handed over from search starts a new conversation instead.
    if (!saved || location.state?.ask) return;
    (async () => {
      try {
        const data = await getSessionMessages(saved);
        setMessages(
          (data.messages || []).map((m, i) => ({
            id: `${saved}-${i}`,
            role: m.role,
            content: m.content,
            records: m.records || [],
            searches: m.searches || [],
            timestamp: null,
          }))
        );
        setSessionId(saved);
      } catch (error) {
        // Saved session no longer exists — drop it and start fresh.
        localStorage.removeItem(CHAT_SESSION_KEY);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist the active conversation whenever it changes (skip the first render
  // so we don't clobber the saved id before the restore above runs).
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (sessionId) localStorage.setItem(CHAT_SESSION_KEY, sessionId);
    else localStorage.removeItem(CHAT_SESSION_KEY);
  }, [sessionId]);

  const abandonPendingAnswer = () => {
    requestRef.current += 1;
    setIsLoading(false);
  };

  const handleNewChat = () => {
    abandonPendingAnswer();
    setMessages([]);
    setSessionId(null);
  };

  const handleSelectSession = async (id) => {
    if (id === sessionId) return;
    abandonPendingAnswer();
    const request = requestRef.current;
    try {
      const data = await getSessionMessages(id);
      if (request !== requestRef.current) return;
      const loaded = (data.messages || []).map((m, i) => ({
        id: `${id}-${i}`,
        role: m.role,
        content: m.content,
        records: m.records || [],
        searches: m.searches || [],
        timestamp: null,
      }));
      setMessages(loaded);
      setSessionId(id);
    } catch (error) {
      toast({
        title: t('toasts.errorTitle'),
        description: t('toasts.loadFailed'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const askDeleteSession = (id, e) => {
    if (e) e.stopPropagation();
    setPendingDelete(id);
    confirmDelete.onOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await clearSession(pendingDelete);
      if (pendingDelete === sessionId) {
        abandonPendingAnswer();
        setMessages([]);
        setSessionId(null);
      }
      await loadSessions();
      toast({
        title: t('toasts.deletedTitle'),
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: t('toasts.errorTitle'),
        description: t('toasts.clearFailed'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setDeleting(false);
      setPendingDelete(null);
      confirmDelete.onClose();
    }
  };

  // `sid` is the conversation to send into; null starts a new one.
  const sendText = async (text, sid = sessionId) => {
    if (!text.trim() || isLoading) return;

    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: text.trim(),
      timestamp: new Date().toISOString(),
      // Sent in this view, not restored: only these offer "Log this".
      sentHere: true,
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
    requestRef.current += 1;
    const request = requestRef.current;

    try {
      const response = await sendMessage(sid, userMessage.content);

      if (request !== requestRef.current) {
        // The user moved on; the answer is still saved in its conversation.
        loadSessions();
        return;
      }

      if (!sid) {
        setSessionId(response.session_id);
      }

      const assistantMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.response,
        // What the answer's rows show and what it changed (MI-343).
        records: response.records || [],
        searches: response.searches || [],
        timestamp: response.timestamp,
      };

      setMessages((prev) => [...prev, assistantMessage]);
      loadSessions();
    } catch (error) {
      if (request !== requestRef.current) return;
      toast({
        title: t('toasts.errorTitle'),
        description: sendFailureText(error, t('toasts.sendFailed')),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      if (request === requestRef.current) setIsLoading(false);
    }
  };

  const handleSendMessage = () => {
    if (!inputValue.trim() || isLoading) return;
    setInputValue('');
    sendText(inputValue);
  };

  // A question handed over from search (`navigate('/chat', { state: { ask } })`)
  // is sent as a new conversation. The state is then cleared, so a reload
  // does not send it again.
  const handledAskKey = useRef(null);
  useEffect(() => {
    const ask = location.state?.ask;
    if (!ask || handledAskKey.current === location.key) return;
    handledAskKey.current = location.key;
    setMessages([]);
    setSessionId(null);
    sendText(ask, null);
    navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Phone: the composer is fixed over the bottom of the list, which pads by
  // its height. It grows with its text, up to five lines. Its bottom padding
  // changes with the keyboard, so watch the border box, and measure again
  // when the keyboard moves in case the observer cannot.
  useLayoutEffect(() => {
    const el = composerRef.current;
    if (!isPhone || !el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => setComposerH(el.offsetHeight));
    observer.observe(el, { box: 'border-box' });
    return () => observer.disconnect();
  }, [isPhone]);

  useLayoutEffect(() => {
    const el = composerRef.current;
    if (isPhone && el) setComposerH(el.offsetHeight);
  }, [isPhone, keyboardUp]);

  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!isPhone || !el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, PHONE_COMPOSER_MAX_H)}px`;
  }, [inputValue, isPhone]);

  // Keep the last message in view as the keyboard or the composer moves.
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (isPhone && el && messages.length > 0) el.scrollTop = el.scrollHeight;
  }, [inset, composerH, isPhone, messages.length]);

  // Tapping the active Ask tab scrolls the conversation, not the window.
  useEffect(() => {
    if (!isPhone) return undefined;
    const toTop = () => messagesContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    window.addEventListener(SCROLL_TOP_EVENT, toTop);
    return () => window.removeEventListener(SCROLL_TOP_EVENT, toTop);
  }, [isPhone]);

  usePhoneTopBar({
    title: <FitTitle>{t('assistant.launcher')}</FitTitle>,
    centered: true,
    left: (
      <IconButton
        icon={<LuHistory size={22} />}
        variant="ghost"
        borderRadius="full"
        color="redesign.text"
        minW="44px"
        minH="44px"
        aria-label={t('assistant.history')}
        onClick={() => { loadSessions(); setHistoryOpen(true); }}
      />
    ),
    right: (
      <IconButton
        icon={<LuPencil size={22} />}
        variant="ghost"
        borderRadius="full"
        color="redesign.text"
        minW="44px"
        minH="44px"
        aria-label={t('newChat')}
        onClick={handleNewChat}
      />
    ),
  }, [t, i18n.language]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Untitled until the first message names the conversation — say so, rather
  // than labelling an empty chat with the product name.
  const activeTitle = sessions.find((s) => s.id === sessionId)?.title || t('newChat');

  // Greet by first name and time of day — a blank page is where the product
  // can feel personal, and "Welcome to Mi Kapital Chat!" was neither.
  const firstName = (user?.full_name || user?.username || '').trim().split(' ')[0];
  const hour = new Date().getHours();
  const partOfDay = hour < 12 ? 'morning' : hour < 19 ? 'afternoon' : 'evening';
  const greeting = firstName
    ? t(`greeting.${partOfDay}Named`, { name: firstName })
    : t(`greeting.${partOfDay}`);

  const isEmpty = messages.length === 0 && !isLoading;

  // Hand the conversation back to the floating panel and return to the page
  // the user expanded from. The flag tells the widget to open it expanded
  // rather than restoring it as one more minimized pill.
  const dockToPanel = () => {
    if (sessionId) localStorage.setItem(DOCK_REQUEST_KEY, sessionId);
    if (window.history.length > 1) navigate(-1);
    else navigate('/movements');
  };

  // A click on a record (MI-343) goes to the record's own page, with the record
  // open. On the desktop this conversation goes along, in the floating panel;
  // on the phone the Ask tab brings it back, as it always has.
  const leaveWithConversation = (to) => {
    if (!isPhone && sessionId) localStorage.setItem(DOCK_REQUEST_KEY, sessionId);
    navigate(to);
  };
  const openRecord = (type, id, options) => {
    const to = recordLocation(type, id, window.location, options);
    if (to) leaveWithConversation(to);
  };
  const openSearch = (search) => {
    showSearch(search);
    leaveWithConversation('/movements');
  };

  if (isPhone) {
    const canSend = inputValue.trim() !== '' && !isLoading;
    const listPb = keyboardUp
      ? `calc(${composerH + inset}px - 49px - env(safe-area-inset-bottom))`
      : `${composerH}px`;

    return (
      // The layout pads the page 16px below the tab bar; this page is exactly
      // one screen, so it takes those 16px back rather than scroll by them.
      <Flex direction="column" h={PHONE_PAGE_H} mb="-16px" w="100%">
        <Box
          ref={messagesContainerRef}
          flex="1"
          minH={0}
          overflowY="auto"
          overscrollBehavior="contain"
          pb={listPb}
        >
          {isEmpty ? (
            <Flex direction="column" minH="100%" px="16px">
              <Flex
                flex="1"
                direction="column"
                align="center"
                justify="center"
                py="24px"
                sx={{ '@media (max-height: 600px)': { paddingTop: '8px', paddingBottom: '8px' } }}
              >
                <Text fontSize="22px" lineHeight="28px" fontWeight="600" color="redesign.heading" textAlign="center">
                  {greeting}
                </Text>
                {/* On a short screen the suggestions matter more than the subtitle. */}
                <Text
                  fontSize="15px"
                  lineHeight="1.4"
                  color="phone.muted"
                  textAlign="center"
                  mt="8px"
                  sx={{ '@media (max-height: 600px)': { display: 'none' } }}
                >
                  {t('assistant.welcomeSubtitle')}
                </Text>
              </Flex>
              {/* A landscape phone has room for two above the composer; the
                  rest would sit half under it. */}
              <VStack
                spacing="8px"
                align="stretch"
                pb="8px"
                sx={{ '@media (max-height: 420px)': { '& > :nth-of-type(n+3)': { display: 'none' } } }}
              >
                {PHONE_SUGGESTIONS.map((key) => (
                  <Flex
                    key={key}
                    as="button"
                    type="button"
                    align="center"
                    gap="12px"
                    minH="52px"
                    pl="16px"
                    pr="14px"
                    py="5px"
                    textAlign="left"
                    bg="redesign.bg.card"
                    borderWidth="1px"
                    borderColor="redesign.border"
                    borderRadius="14px"
                    color="redesign.heading"
                    onClick={() => sendText(suggestion(key))}
                    _active={{ bg: 'redesign.bg.bubble' }}
                    sx={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <Box as="span" flex="1" minW={0} fontSize="15px" lineHeight="1.3">{suggestion(key)}</Box>
                    <Box as={LuArrowUp} boxSize="18px" color="phone.muted" flexShrink={0} />
                  </Flex>
                ))}
              </VStack>
            </Flex>
          ) : (
            // An answer sits close under its question; a new question starts
            // a new exchange, with more room above it.
            <ChatRecordsProvider
              messages={messages}
              onOpenRecord={openRecord}
              onOpenSearch={openSearch}
              phone
            >
            <VStack spacing={0} align="stretch" px="16px" pt="12px" pb="8px">
              {messages.map((message, i) => (
                message.role === 'user' ? (
                  <Flex key={message.id} direction="column" align="flex-end" mt={i === 0 ? 0 : '20px'}>
                    <Box
                      maxW="78%"
                      bg="redesign.bg.accent"
                      color="redesign.heading"
                      px="14px"
                      py="9px"
                      borderRadius="18px"
                      borderBottomRightRadius="4px"
                    >
                      <Text textStyle="phoneBody" whiteSpace="pre-wrap">{message.content}</Text>
                    </Box>
                    {message.sentHere && can('movement.create') && looksLikeSpending(message.content) && (
                      <Button
                        variant="ghost"
                        minH="44px"
                        px="4px"
                        mt="2px"
                        color="phone.link"
                        fontSize="15px"
                        fontWeight="500"
                        leftIcon={<LuPlus size={18} />}
                        iconSpacing="6px"
                        onClick={() => openQuickAdd({ mode: 'describe', describeText: message.content })}
                      >
                        {t('phone.logThis')}
                      </Button>
                    )}
                  </Flex>
                ) : (
                  <Box key={message.id} color="redesign.text" mt={i === 0 ? 0 : '8px'}>
                    <ChatMarkdown
                      content={message.content}
                      records={message.records}
                      searches={message.searches}
                      position={i}
                      phone
                    />
                  </Box>
                )
              ))}
              {isLoading && (
                <HStack spacing="8px" color="phone.muted" mt={messages.length ? '8px' : 0}>
                  <Spinner size="sm" />
                  <Text textStyle="phoneBody">{t('thinking')}</Text>
                </HStack>
              )}
            </VStack>
            </ChatRecordsProvider>
          )}
        </Box>

        {/* Fixed above the tab bar, or on top of the keyboard while it is up.
            Return inserts a newline, as in Messages; Send sends. */}
        <Box
          ref={composerRef}
          position="fixed"
          left={0}
          right={0}
          bottom={keyboardUp ? `${inset}px` : 'calc(49px + env(safe-area-inset-bottom))'}
          zIndex={2}
          bg="redesign.bg.page"
          pt="8px"
          // Clear of the tab bar's raised + (16px up, with a 4px ring).
          pb={keyboardUp ? '8px' : '26px'}
          pl="calc(16px + env(safe-area-inset-left))"
          pr="calc(16px + env(safe-area-inset-right))"
        >
          <Flex
            align="flex-end"
            bg="redesign.bg.card"
            borderWidth="1px"
            borderColor="redesign.border"
            borderRadius="23px"
            _focusWithin={{ borderColor: 'redesign.border.strong' }}
            pl="14px"
            pr="1px"
            py={0}
          >
            <Textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={t('assistant.placeholder')}
              enterKeyHint="enter"
              variant="unstyled"
              resize="none"
              rows={1}
              minH="44px"
              py="11px"
              px={0}
              fontSize="16px"
              lineHeight="22px"
              overflowY="auto"
              color="redesign.heading"
              _placeholder={{ color: 'phone.muted' }}
              flex="1"
            />
            <IconButton
              aria-label={t('send')}
              icon={<LuArrowUp size={22} />}
              // Keep the field focused, so the keyboard stays up for the next
              // question.
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleSendMessage}
              isLoading={isLoading}
              isDisabled={!canSend}
              w="44px"
              h="44px"
              minW="44px"
              borderRadius="full"
              bg={canSend ? 'redesign.accent.fill' : 'redesign.border'}
              color={canSend ? 'white' : 'phone.muted'}
              // The shades the theme's filled button steps to on hover and
              // press; there is no token for them.
              _hover={{ bg: canSend ? '#1D4ED8' : 'redesign.border' }}
              _active={{ bg: canSend ? '#1E40AF' : 'redesign.border' }}
              // Disabled, the arrow keeps phone.muted, as the placeholder does.
              _disabled={{ opacity: 1, cursor: 'default' }}
            />
          </Flex>
        </Box>

        <PhoneChatHistory
          isOpen={historyOpen}
          onClose={() => setHistoryOpen(false)}
          sessions={sessions}
          activeId={sessionId}
          onSelect={(id) => { setHistoryOpen(false); handleSelectSession(id); }}
          onDelete={(id) => askDeleteSession(id)}
        />
        <PhoneConfirmSheet
          isOpen={confirmDelete.isOpen}
          title={t('deleteDialog.title')}
          body={t('deleteDialog.message')}
          confirmLabel={t('deleteDialog.confirm')}
          destructive
          onConfirm={handleDeleteConfirm}
          onCancel={() => { setPendingDelete(null); confirmDelete.onClose(); }}
        />
      </Flex>
    );
  }

  // One composer, placed either in the centered empty state or docked below
  // the conversation — so its look can never drift between the two.
  const composer = (
    <Flex
      maxW="4xl"
      mx="auto"
      align="flex-end"
      gap={2}
      bg="redesign.bg.card"
      border="1px solid"
      borderColor="redesign.border"
      borderRadius="2xl"
      boxShadow="card"
      px={4}
      py={3}
    >
      <Textarea
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyPress={handleKeyPress}
        placeholder={owner ? t('inputPlaceholderFor', { owner }) : t('inputPlaceholder')}
        variant="unstyled"
        resize="none"
        rows={1}
        minH="52px"
        maxH="200px"
        py={3}
        px={1}
        fontSize="md"
        isDisabled={isLoading}
        flex="1"
      />
      <IconButton
        aria-label={t('send')}
        icon={<ArrowUpIcon />}
        onClick={handleSendMessage}
        isLoading={isLoading}
        isDisabled={!inputValue.trim() || isLoading}
        // Quiet like Linear's: the composer's own surface with a hairline,
        // greying on hover, rather than a coloured call to action.
        bg="redesign.bg.surface"
        color="redesign.heading"
        borderWidth="1px"
        borderColor="redesign.border"
        _hover={{ bg: 'redesign.bg.bubble' }}
        borderRadius="full"
        size="lg"
      />
    </Flex>
  );

  return (
    <Flex
      direction="column"
      minH={{ base: 'calc(100vh - 120px)', md: 'calc(var(--mk-panel-h) - 64px)' }}
      maxH={{ base: 'calc(100vh - 120px)', md: 'calc(var(--mk-panel-h) - 64px)' }}
      w="100%"
      p={{ base: 2, md: 4 }}
    >
      {/* Header — a real bar spanning the page, so the title sits *on* it
          rather than floating over the conversation. The title doubles as the
          history menu; ··· holds the actions for the chat itself; the dock
          button on the right sends it back to the floating panel. Flush left
          on purpose: indented to the message column it reads as adrift. */}
      <Flex
        align="center"
        gap={1}
        px={{ base: 1, md: 2 }}
        py={1}
        mx={{ base: -2, md: -4 }}
        mt={{ base: -2, md: -4 }}
        mb={2}
        pl={{ base: 3, md: 6 }}
        pr={{ base: 3, md: 6 }}
        borderBottomWidth="1px"
        borderColor="redesign.border"
      >
        <Menu placement="bottom-start" isLazy onOpen={loadSessions}>
          <MenuButton
            as={Button}
            variant="ghost"
            size="sm"
            rightIcon={<LuChevronDown />}
            // Short enough that the cut reads as deliberate: a title is
            // usually 3–6 AI-generated words, and only the fallback (the raw
            // first message) ever runs long. The full text is on hover.
            maxW={{ base: '55vw', md: '280px' }}
            title={activeTitle}
            // Pill-shaped hover that hugs the title, rather than a squared
            // button block. Chakra's ghost button also falls back to gray.200
            // while its menu is open — a cool grey that reads blue here.
            borderRadius="full"
            px={3}
            _hover={{ bg: 'redesign.bg.bubble' }}
            _active={{ bg: 'redesign.bg.bubble' }}
          >
            <Text fontSize="md" fontWeight="600" color="redesign.heading" noOfLines={1}>
              {activeTitle}
            </Text>
          </MenuButton>
          {/* Bounded so long titles can't stretch it across the page, but with
              room to breathe: inset rounded rows, sentence-case section
              labels, no dividers (Linear-style). */}
          <MenuList
            maxH="420px"
            overflowY="auto"
            minW="260px"
            maxW="340px"
            py={2}
            px={2}
            borderRadius="xl"
          >
            <Text px={2} pt={1} pb={2} fontSize="sm" color="redesign.text.muted">
              {t('history.title')}
            </Text>
            <MenuItem
              icon={<AddIcon />}
              onClick={handleNewChat}
              fontSize="sm"
              py={2}
              px={2}
              borderRadius="md"
              _hover={MENU_HIGHLIGHT}
              _focus={MENU_HIGHLIGHT}
            >
              {t('newChat')}
            </MenuItem>
            {sessions.length === 0 ? (
              <Text px={3} py={3} fontSize="sm" color="redesign.text.muted">
                {t('history.empty')}
              </Text>
            ) : (
              groupSessions(sessions).map((group) => (
                <Box key={group.key}>
                  <Text
                    px={2}
                    pt={3}
                    pb={1}
                    fontSize="xs"
                    color="redesign.text.muted"
                  >
                    {t(`history.${group.key}`)}
                  </Text>
                  {group.items.map((s) => {
                    const active = s.id === sessionId;
                    return (
                      <MenuItem
                        key={s.id}
                        py={2}
                        px={2}
                        borderRadius="md"
                        _hover={MENU_HIGHLIGHT}
                        _focus={MENU_HIGHLIGHT}
                        onClick={() => handleSelectSession(s.id)}
                      >
                        {/* `role="group"` has to live here: Chakra overwrites
                            MenuItem's role with "menuitem", which silently
                            breaks every `_groupHover` inside it. */}
                        <Flex role="group" align="center" gap={4} w="100%" minW={0} position="relative">
                          <Text fontSize="sm" noOfLines={1} flex="1" minW={0} fontWeight={active ? '600' : '400'}>
                            {s.title}
                          </Text>
                          {active && (
                            <Text fontSize="xs" color="redesign.text.muted" flexShrink={0}>
                              {t('history.current')}
                            </Text>
                          )}
                          <Text
                            fontSize="xs"
                            color="redesign.text.muted"
                            flexShrink={0}
                            _groupHover={{ opacity: { base: 1, md: 0 } }}
                          >
                            {formatRelativeShort(s.updated_at)}
                          </Text>
                          {/* Sits over the age instead of beside it: hidden by
                              opacity it would still reserve its width, leaving
                              a dead strip down the right of every row.
                              `as="span"`: MenuItem already renders a <button>,
                              and nesting one inside is invalid HTML. */}
                          <IconButton
                            as="span"
                            icon={<DeleteIcon />}
                            aria-label={t('history.deleteAria')}
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            // Touch has no hover: there it stays in the flow,
                            // always visible, as before.
                            position={{ base: 'static', md: 'absolute' }}
                            right={0}
                            top={{ md: '50%' }}
                            transform={{ md: 'translateY(-50%)' }}
                            flexShrink={0}
                            opacity={{ base: 1, md: 0 }}
                            _groupHover={{ opacity: 1 }}
                            onClick={(e) => askDeleteSession(s.id, e)}
                          />
                        </Flex>
                      </MenuItem>
                    );
                  })}
                </Box>
              ))
            )}
          </MenuList>
        </Menu>

        <Menu placement="bottom-start" isLazy>
          <MenuButton
            as={IconButton}
            icon={<LuEllipsis />}
            variant="ghost"
            size="sm"
            isDisabled={!sessionId}
            aria-label={t('assistant.more')}
            _hover={{ bg: 'redesign.bg.bubble' }}
            _active={{ bg: 'redesign.bg.bubble' }}
          />
          <MenuList minW="180px">
            <MenuItem
              icon={<LuTrash2 />}
              color="redesign.accent.red"
              _hover={MENU_HIGHLIGHT}
              _focus={MENU_HIGHLIGHT}
              onClick={() => askDeleteSession(sessionId)}
            >
              {t('assistant.delete')}
            </MenuItem>
          </MenuList>
        </Menu>

        {/* Back to the floating panel: hand the conversation to the widget and
            return to whatever page the user expanded from. */}
        <IconButton
          ml="auto"
          icon={<DockIcon boxSize={4} />}
          variant="ghost"
          size="sm"
          aria-label={t('assistant.dock')}
          title={t('assistant.dock')}
          _hover={{ bg: 'redesign.bg.bubble' }}
          onClick={dockToPanel}
        />
      </Flex>

      {/* Empty chat: greeting and composer read as one centered composition,
          instead of a greeting adrift in the middle with the input pinned to
          the bottom of an otherwise empty page. */}
      {isEmpty ? (
        <Flex flex="1" direction="column" align="center" justify="center" w="100%" px={{ base: 3, md: 4 }}>
          <Text
            fontSize={{ base: '2xl', md: '3xl' }}
            fontWeight="500"
            color="redesign.heading"
            textAlign="center"
            mb={2}
          >
            👋 {greeting}
          </Text>
          <Text fontSize="md" color="redesign.text.muted" textAlign="center" mb={6}>
            {owner ? t('welcomeSubtitleFor', { owner }) : t('welcomeSubtitle')}
          </Text>
          <Box w="100%">{composer}</Box>
        </Flex>
      ) : (
      <Box ref={messagesContainerRef} flex="1" overflowY="auto" w="100%">
        <ChatRecordsProvider
          messages={messages}
          onOpenRecord={openRecord}
          onOpenSearch={openSearch}
        >
        <VStack
          maxW="4xl"
          mx="auto"
          spacing={6}
          align="stretch"
          px={{ base: 3, md: 4 }}
          py={4}
        >
          {messages.map((message, i) => (
            <Flex
              key={message.id}
              justify={message.role === 'user' ? 'flex-end' : 'flex-start'}
              w="100%"
            >
              <Box
                maxW={message.role === 'user' ? '85%' : '100%'}
                // Neutral, not branded: the user's own words don't need to
                // shout over the answer they're reading (as in Linear, Claude
                // and ChatGPT).
                bg={message.role === 'user' ? 'redesign.bg.bubble' : 'transparent'}
                color="redesign.heading"
                px={message.role === 'user' ? 4 : 0}
                py={message.role === 'user' ? 3 : 0}
                borderRadius="2xl"
                borderBottomRightRadius={message.role === 'user' ? 'sm' : '2xl'}
              >
                {message.role === 'assistant' ? (
                  <ChatMarkdown
                    content={message.content}
                    records={message.records}
                    searches={message.searches}
                    position={i}
                  />
                ) : (
                  <Text whiteSpace="pre-wrap">{message.content}</Text>
                )}
              </Box>
            </Flex>
          ))}

          {isLoading && (
            <Flex justify="flex-start" w="100%">
              <HStack spacing={2} align="center" color="redesign.text.muted">
                <Spinner size="sm" />
                <Text>{t('thinking')}</Text>
              </HStack>
            </Flex>
          )}
        </VStack>
        </ChatRecordsProvider>
      </Box>
      )}

      {/* Composer, docked at the bottom once the conversation has started. */}
      {!isEmpty && (
        <Box w="100%" px={{ base: 2, md: 4 }} pt={2} pb={{ base: 3, md: 5 }}>
          {composer}
        </Box>
      )}

      <ConfirmationDialog
        isOpen={confirmDelete.isOpen}
        onClose={() => { setPendingDelete(null); confirmDelete.onClose(); }}
        onConfirm={handleDeleteConfirm}
        title={t('deleteDialog.title')}
        message={t('deleteDialog.message')}
        confirmText={t('deleteDialog.confirm')}
        confirmColorScheme="red"
        isLoading={deleting}
      />
    </Flex>
  );
};


export default ChatPage;
